import type { Event, EventType } from "../reducers/apiTypes";

export type CategoryColor = "pink" | "green" | "orange" | "purple" | "blue";

const CATEGORY_COLORS: Record<string, CategoryColor> = {
    CONCERT: "pink",
    COMEDY: "purple",
    COMMUNITY: "green",
    SPORTS: "green",
    TRIVIA: "purple",
    KIDS: "orange",
    EDUCATION: "blue",
    ART: "purple",
    FESTIVAL: "pink",
    OTHER: "orange",
    VOLUNTEER: "green",
    FOOD: "orange",
    NETWORKING: "blue",
    BAR: "orange",
    HAPPY_HOUR: "orange",
};

const CATEGORY_LABELS: Record<string, string> = {
    CONCERT: "Music",
    COMEDY: "Comedy",
    COMMUNITY: "Community",
    SPORTS: "Sports",
    TRIVIA: "Trivia",
    KIDS: "Kids",
    EDUCATION: "Education",
    ART: "Art",
    FESTIVAL: "Festival",
    OTHER: "Other",
    VOLUNTEER: "Volunteer",
    FOOD: "Food & Drink",
    NETWORKING: "Networking",
    BAR: "Food & Drink",
    HAPPY_HOUR: "Happy Hour",
};

const EVENT_TYPE_EMOJIS: Record<string, string> = {
    CONCERT: "🎵",
    COMEDY: "🎤",
    COMMUNITY: "🏘️",
    SPORTS: "⚽",
    TRIVIA: "🧠",
    KIDS: "👶",
    EDUCATION: "📚",
    ART: "🎨",
    FESTIVAL: "🎪",
    OTHER: "📅",
    VOLUNTEER: "🤝",
    FOOD: "🍽️",
    NETWORKING: "💼",
    BAR: "🍸",
    HAPPY_HOUR: "🍻",
};

export function eventTypeEmoji(eventType: EventType | string): string {
    return EVENT_TYPE_EMOJIS[eventType.toUpperCase()] ?? "📅";
}

export function categoryColor(eventType: EventType | string): CategoryColor {
    return CATEGORY_COLORS[eventType.toUpperCase()] ?? "orange";
}

export function categoryLabel(eventType: EventType | string): string {
    return CATEGORY_LABELS[eventType.toUpperCase()] ?? eventType;
}

export function formatEventTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
    });
}

export function formatEventDateHeader(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
}

export function formatEventDateLong(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
    });
}

const ISO_WEEKDAY_SHORT = [
    "",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sun",
] as const;

const ISO_WEEKDAY_LONG = [
    "",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
] as const;

export function formatDayOfWeeksShort(days: number[]): string {
    return days
        .filter((d) => d >= 1 && d <= 7)
        .map((d) => ISO_WEEKDAY_SHORT[d])
        .join(", ");
}

export function formatDayOfWeeksLong(days: number[]): string {
    return days
        .filter((d) => d >= 1 && d <= 7)
        .map((d) => ISO_WEEKDAY_LONG[d])
        .join(", ");
}

const DEFAULT_DURATION_MINUTES = 90;

export function hasEventEndDatetime(event: Event): boolean {
    if (!event.end_datetime) return false;
    const start = new Date(event.datetime).getTime();
    const end = new Date(event.end_datetime).getTime();
    return (
        !Number.isNaN(start) && !Number.isNaN(end) && end > start
    );
}

/** Resolved end time: real end_datetime when set, otherwise start + default duration. */
export function getEventEndIso(event: Event): string {
    if (hasEventEndDatetime(event)) return event.end_datetime!;
    const end = new Date(event.datetime);
    if (Number.isNaN(end.getTime())) return event.datetime;
    end.setMinutes(end.getMinutes() + DEFAULT_DURATION_MINUTES);
    return end.toISOString();
}

export function eventDateKey(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export function formatEventDateShort(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

export function isMultiDayEvent(event: Event): boolean {
    if (!hasEventEndDatetime(event)) return false;
    return eventDateKey(event.datetime) !== eventDateKey(event.end_datetime!);
}

export function formatEventOngoingRange(event: Event): string {
    const start = formatEventDateShort(event.datetime);
    const end = formatEventDateShort(event.end_datetime!);
    if (start === end) return `Ongoing · ${start}`;
    return `Ongoing · ${start} – ${end}`;
}

export function formatEventOngoingRangeLong(event: Event): string {
    const start = formatEventDateLong(event.datetime);
    const end = formatEventDateLong(event.end_datetime!);
    if (start === end) return `Ongoing · ${start}`;
    return `Ongoing · ${start} – ${end}`;
}

export function formatEventTimeRange(event: Event): string {
    if (isMultiDayEvent(event)) return formatEventOngoingRange(event);
    const start = formatEventTime(event.datetime);
    if (!hasEventEndDatetime(event)) return start;
    return `${start} – ${formatEventTime(event.end_datetime!)}`;
}

export function formatEventWhenShort(event: Event): string {
    if (isMultiDayEvent(event)) return formatEventOngoingRange(event);
    const d = new Date(event.datetime);
    if (Number.isNaN(d.getTime())) return event.datetime;
    const timeRange = formatEventTimeRange(event);
    if (event.is_recurring && event.day_of_weeks?.length) {
        return `${formatDayOfWeeksShort(event.day_of_weeks)} · ${timeRange}`;
    }
    const day = d.toLocaleDateString("en-US", { weekday: "short" });
    return `${day} · ${timeRange}`;
}

export function formatEventScheduleLong(event: Event): string {
    if (isMultiDayEvent(event)) return formatEventOngoingRangeLong(event);
    const timeRange = formatEventTimeRange(event);
    if (event.is_recurring && event.day_of_weeks?.length) {
        return `Every ${formatDayOfWeeksLong(event.day_of_weeks)} · ${timeRange}`;
    }
    return `${formatEventDateLong(event.datetime)} · ${timeRange}`;
}

export function formatDateRangeShort(
    from: string | null,
    to: string | null,
    options?: { today?: boolean; weekend?: boolean }
): string {
    if (options?.today) return "Today";
    if (options?.weekend) return "This weekend";
    if (!from || !to) return "Any date";
    const start = new Date(from + "T12:00:00");
    const end = new Date(to + "T12:00:00");
    const fmt = (d: Date) =>
        d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(start)} – ${fmt(end)}`;
}

export function estimateDurationMinutes(event: Event): number {
    if (hasEventEndDatetime(event)) {
        const start = new Date(event.datetime).getTime();
        const end = new Date(event.end_datetime!).getTime();
        return Math.max(1, Math.round((end - start) / 60_000));
    }
    return DEFAULT_DURATION_MINUTES;
}

export function formatEventDurationLabel(event: Event): string {
    if (isMultiDayEvent(event)) return "Ongoing";
    return `${estimateDurationMinutes(event)} min`;
}

export function groupEventsByDate(events: Event[]): Map<string, Event[]> {
    const sorted = [...events].sort(
        (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
    );
    const groups = new Map<string, Event[]>();
    for (const event of sorted) {
        const key = eventDateKey(event.datetime);
        const bucket = groups.get(key);
        if (bucket) bucket.push(event);
        else groups.set(key, [event]);
    }
    return groups;
}

export function filterByTimeOfDay(
    events: Event[],
    timeOfDay: "morning" | "afternoon" | "evening" | null
): Event[] {
    if (!timeOfDay) return events;
    return events.filter((e) => {
        const hour = new Date(e.datetime).getHours();
        if (timeOfDay === "morning") return hour < 12;
        if (timeOfDay === "afternoon") return hour >= 12 && hour < 17;
        return hour >= 17;
    });
}

export const WEEKDAY_LETTERS = [
    { id: 2, letter: "M" },
    { id: 3, letter: "T" },
    { id: 4, letter: "W" },
    { id: 5, letter: "T" },
    { id: 6, letter: "F" },
    { id: 7, letter: "S" },
    { id: 1, letter: "S" },
] as const;
